import { Box, TableCell, TableRow, Typography } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import React from "react";

interface UpRowProps {
  /** Name of the folder one level up ("All projects" at the top). */
  parentName: string;
  colSpan: number;
  onClick: () => void;
  /** Set while a row is being dragged: the row is then a drop target, not a link. */
  isDragging?: boolean;
  isDropTarget?: boolean;
  dropProps?: React.HTMLAttributes<HTMLElement>;
}

/**
 * The "up one level" row at the top of a folder's contents, like a file
 * manager's "..". Always present inside a folder: a way up and, where the
 * table supports it, the drop target for moving things out. It must NOT
 * appear only while dragging — inserting a row during dragstart shifts the
 * dragged row and makes the browser abandon the drag.
 */
export default function UpRow({
  parentName,
  colSpan,
  onClick,
  isDragging = false,
  isDropTarget = false,
  dropProps,
}: UpRowProps) {
  return (
    <TableRow
      hover={!isDragging}
      onClick={() => {
        if (!isDragging) onClick();
      }}
      {...(dropProps ?? {})}
      sx={{
        cursor: isDragging ? undefined : "pointer",
        backgroundColor: isDropTarget ? "rgba(0,212,173,0.14)" : "rgba(0,0,0,0.02)",
        outline: isDropTarget ? "2px solid #00d4ad" : isDragging ? "2px dashed #d1d5db" : "none",
        outlineOffset: -2,
      }}
    >
      <TableCell colSpan={colSpan} sx={{ py: 0.75 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
          <ArrowUpwardIcon fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {parentName}
          </Typography>
          <Typography variant="caption">
            {isDragging ? `Drop here to move to “${parentName}”` : "Up one level"}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );
}
